import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const WatchContext = createContext(null);

export function WatchProvider({ children }) {
  const { user } = useAuth();
  const [watched, setWatched] = useState(new Set());
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [checking, setChecking] = useState(false);
  const pollRef = useRef(null);

  const loadWatchlist = useCallback(() => {
    if (!user) {
      setWatched(new Set());
      return;
    }
    api.get("/watchlist").then((r) => setWatched(new Set(r.data.map((e) => e.prop_id)))).catch(() => {});
  }, [user]);

  const refreshAlerts = useCallback(() => {
    if (!user) {
      setAlerts([]);
      setUnread(0);
      return;
    }
    api.get("/alerts").then((r) => {
      setAlerts(r.data.alerts || []);
      setUnread(r.data.unread || 0);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    loadWatchlist();
    refreshAlerts();
  }, [loadWatchlist, refreshAlerts]);

  useEffect(() => {
    if (!user) return;
    pollRef.current = setInterval(refreshAlerts, 60000);
    return () => clearInterval(pollRef.current);
  }, [user, refreshAlerts]);

  const isWatched = useCallback((propId) => watched.has(propId), [watched]);

  const toggleWatch = useCallback(
    async (prop) => {
      if (!user) {
        toast.error("Sign in to track line movement");
        return { needAuth: true };
      }
      const already = watched.has(prop.id);
      setWatched((prev) => {
        const next = new Set(prev);
        already ? next.delete(prop.id) : next.add(prop.id);
        return next;
      });
      try {
        if (already) {
          await api.delete(`/watchlist/${prop.id}`);
          toast.info(`Stopped tracking ${prop.player_name} ${prop.metric}`);
        } else {
          await api.post("/watchlist", { prop_id: prop.id });
          toast.success(`Tracking ${prop.player_name} ${prop.metric} for line moves`);
        }
      } catch (e) {
        loadWatchlist();
      }
      return {};
    },
    [user, watched, loadWatchlist]
  );

  const markRead = useCallback(async () => {
    if (!user || unread === 0) return;
    setUnread(0);
    try {
      await api.post("/alerts/read");
    } catch (_) {}
  }, [user, unread]);

  const checkNow = useCallback(async () => {
    if (!user) {
      toast.error("Sign in to check for line movement");
      return;
    }
    setChecking(true);
    try {
      const r = await api.post("/watchlist/check");
      if (r.data.checked === 0) toast.info("Star some props first to track their lines");
      else if (r.data.alerts > 0) toast.success(`${r.data.alerts} line move(s) detected!`);
      else toast.info("No line movement since last check");
      refreshAlerts();
      loadWatchlist();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not check for movement");
    } finally {
      setChecking(false);
    }
  }, [user, refreshAlerts, loadWatchlist]);

  return (
    <WatchContext.Provider
      value={{ watched, isWatched, toggleWatch, alerts, unread, refreshAlerts, markRead, checkNow, checking }}
    >
      {children}
    </WatchContext.Provider>
  );
}

export const useWatch = () => useContext(WatchContext);
